import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Thermometer, Droplets, Zap, Sun } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { motion } from 'framer-motion';
import { useDeviceStore } from '../../stores/deviceStore';
import { DeviceIcon } from '../common/DeviceIcon';
import { mockDeviceStatistics } from '../../api/mockData';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type StatType = 'temperature' | 'humidity' | 'power' | 'brightness';

export function StatisticsScreen() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { devices, fetchDevices } = useDeviceStore();
  
  const [device, setDevice] = useState(devices.find(d => d.deviceId === deviceId));
  const [selectedStat, setSelectedStat] = useState<StatType>('temperature');
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');

  useEffect(() => {
    if (devices.length === 0) {
      fetchDevices();
    }
  }, [devices.length, fetchDevices]);

  useEffect(() => {
    setDevice(devices.find(d => d.deviceId === deviceId));
  }, [devices, deviceId]);

  if (!device) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Устройство не найдено</p>
          <button
            onClick={() => navigate('/devices')}
            className="text-blue-500 hover:underline mt-2"
          >
            Назад к устройствам
          </button>
        </div>
      </div>
    );
  }

  const availableStats = getAvailableStats(device);

  const chartData = {
    labels: mockDeviceStatistics[selectedStat].map(d => {
      const date = new Date(d.timestamp);
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }),
    datasets: [
      {
        label: getStatLabel(selectedStat),
        data: mockDeviceStatistics[selectedStat].map(d => d.value),
        borderColor: getStatColor(selectedStat),
        backgroundColor: `${getStatColor(selectedStat)}20`,
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 6,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#9ca3af',
          maxTicksLimit: 8
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          color: '#9ca3af'
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index' as const
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-white/80 backdrop-blur-lg shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Статистика</h1>
            <div className="w-10" />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-sm"
        >
          <div className="flex items-center gap-4">
            <DeviceIcon type={device.type} isOnline={device.isOnline} size="lg" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {device.customName || device.baseName}
              </h2>
              <p className="text-gray-500">{device.room}</p>
            </div>
          </div>
        </motion.div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {['24h', '7d', '30d'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range as any)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all
                ${timeRange === range
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
                }
              `}
            >
              {range === '24h' ? '24 часа' : range === '7d' ? '7 дней' : '30 дней'}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {availableStats.map((stat) => (
            <motion.button
              key={stat.type}
              onClick={() => setSelectedStat(stat.type)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`
                bg-white rounded-xl p-4 shadow-sm transition-all
                ${selectedStat === stat.type 
                  ? 'ring-2 ring-blue-500 shadow-md' 
                  : 'hover:shadow-md'
                }
              `}
            >
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                <span className="text-sm text-gray-500">{stat.label}</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
            </motion.button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-sm"
        >
          <h3 className="font-semibold text-gray-900 mb-4">
            {getStatLabel(selectedStat)} за последние 24 часа
          </h3>
          <div className="h-64">
            <Line data={chartData} options={chartOptions} />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 shadow-sm"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Сводка</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Среднее</p>
              <p className="text-xl font-bold text-gray-900">
                {calculateAverage(mockDeviceStatistics[selectedStat])}{getStatUnit(selectedStat)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Мин</p>
              <p className="text-xl font-bold text-green-600">
                {calculateMin(mockDeviceStatistics[selectedStat])}{getStatUnit(selectedStat)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Макс</p>
              <p className="text-xl font-bold text-red-600">
                {calculateMax(mockDeviceStatistics[selectedStat])}{getStatUnit(selectedStat)}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function getAvailableStats(device: any): { type: StatType; label: string; icon: any; color: string; value: string }[] {
  const stats: { type: StatType; label: string; icon: any; color: string; value: string }[] = [];
  
  device.parameters.forEach((param: any) => {
    if (param.name.includes('temperature')) {
      stats.push({
        type: 'temperature',
        label: 'Температура',
        icon: Thermometer,
        color: 'text-red-500',
        value: `${param.numericValue}°C`
      });
    }
    if (param.name.includes('humidity')) {
      stats.push({
        type: 'humidity',
        label: 'Влажность',
        icon: Droplets,
        color: 'text-blue-500',
        value: `${param.numericValue}%`
      });
    }
    if (param.name.includes('power_consumption')) {
      stats.push({
        type: 'power',
        label: 'Потребление',
        icon: Zap,
        color: 'text-yellow-500',
        value: `${param.numericValue} Вт`
      });
    }
    if (param.name.includes('brightness')) {
      stats.push({
        type: 'brightness',
        label: 'Яркость',
        icon: Sun,
        color: 'text-amber-500',
        value: `${param.numericValue}%`
      });
    }
  });

  if (stats.length === 0) {
    stats.push({
      type: 'temperature',
      label: 'Температура',
      icon: Thermometer,
      color: 'text-red-500',
      value: '23°C'
    });
  }

  return stats;
}

function getStatLabel(stat: StatType): string {
  switch (stat) {
    case 'temperature': return 'Температура';
    case 'humidity': return 'Влажность';
    case 'power': return 'Потребление';
    case 'brightness': return 'Яркость';
  }
}

function getStatColor(stat: StatType): string {
  switch (stat) {
    case 'temperature': return '#ef4444';
    case 'humidity': return '#3b82f6';
    case 'power': return '#eab308';
    case 'brightness': return '#f59e0b';
  }
}

function getStatUnit(stat: StatType): string {
  switch (stat) {
    case 'temperature': return '°C';
    case 'humidity': return '%';
    case 'power': return ' Вт';
    case 'brightness': return '%';
  }
}

function calculateAverage(data: { value: number }[]): string {
  if (data.length === 0) return '0';
  const avg = data.reduce((sum, d) => sum + d.value, 0) / data.length;
  return avg.toFixed(1);
}

function calculateMin(data: { value: number }[]): string {
  if (data.length === 0) return '0';
  return Math.min(...data.map(d => d.value)).toFixed(1);
}

function calculateMax(data: { value: number }[]): string {
  if (data.length === 0) return '0';
  return Math.max(...data.map(d => d.value)).toFixed(1);
}
